import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { supabase } from '../lib/supabase';
import { BadgeProgressRing, getBadgeState, BADGE_CONFIGS, LEVEL_COLORS } from './BadgeProgressRing';
import ChaseThumbsUp from '../assets/images/ChaseThumbsUp.png';

const SELECT_SOUND = process.env.PUBLIC_URL + '/music/Select.mp3';

const BADGE_MESSAGES = {
  trader: (level) => [
    "Congrats! You've reached",
    "",
    `LEVEL ${level} TRADER`,
    "",
    "Keep trading to level up!",
  ],
  officer: (level) => [
    "Congrats! You've reached",
    "",
    `LEVEL ${level} OFFICER`,
    "",
    "Thanks for keeping",
    "the community safe!",
  ],
  researcher: (level) => [
    "Congrats! You've reached",
    "",
    `LEVEL ${level} RESEARCHER`,
    "",
    "Thanks for building",
    "the card database!",
  ],
  collector: () => [
    "Congrats!",
    "",
    "COLLECTOR",
    "",
    "You've collected 100 cards!",
  ],
  organized: () => [
    "Congrats!",
    "",
    "ORGANIZER",
    "",
    "10 binders with 10+ cards!",
    "That's some serious order!",
  ],
  charter: () => [
    "Welcome!",
    "",
    "CHARTER MEMBER",
    "",
    "You completed the Challenge!",
    "Show this to your Trainer",
    "Center for a reward!",
  ],
};

export default function BadgeCongrats() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const navigate = useNavigate();

  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [displayedText, setDisplayedText] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);

  const playClickSound = useCallback(() => {
    if (localStorage.getItem('soundEnabled') === 'false') return;
    try {
      const audio = new Audio(SELECT_SOUND);
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (err) {}
  }, []);

  // Check for unseen badge notifications
  const checkNotifications = useCallback(async () => {
    if (!activeProfile?.id) return;

    const { data, error } = await supabase
      .from('badge_notifications')
      .select('*')
      .eq('user_id', activeProfile.id)
      .eq('seen', false)
      .order('created_at', { ascending: true });

    if (error || !data || data.length === 0) return;

    setQueue(data);
  }, [activeProfile?.id]);

  // Poll on mount and when profile changes
  useEffect(() => {
    checkNotifications();

    // Also check periodically (every 30s) for async badges
    const interval = setInterval(checkNotifications, 30000);
    return () => clearInterval(interval);
  }, [checkNotifications]);

  // Process queue
  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue(prev => prev.slice(1));
    }
  }, [current, queue]);

  // Typewriter effect
  useEffect(() => {
    if (!current) {
      setDisplayedText('');
      setIsTypingComplete(false);
      setButtonsVisible(false);
      setContentVisible(false);
      return;
    }

    setContentVisible(true);
    setDisplayedText('');
    setIsTypingComplete(false);
    setButtonsVisible(false);

    const msgFn = BADGE_MESSAGES[current.badge_type];
    if (!msgFn) return;

    const lines = msgFn(current.new_level);
    const fullText = lines.join('\n');
    let charIndex = 0;

    const timer = setInterval(() => {
      charIndex++;
      setDisplayedText(fullText.slice(0, charIndex));
      if (charIndex >= fullText.length) {
        clearInterval(timer);
        setIsTypingComplete(true);
        setTimeout(() => setButtonsVisible(true), 200);
      }
    }, 25);

    return () => clearInterval(timer);
  }, [current]);

  // Skip typewriter on tap
  const handleTextTap = () => {
    if (!current || isTypingComplete) return;
    const msgFn = BADGE_MESSAGES[current.badge_type];
    if (!msgFn) return;
    setDisplayedText(msgFn(current.new_level).join('\n'));
    setIsTypingComplete(true);
    setTimeout(() => setButtonsVisible(true), 100);
  };

  // Dismiss current notification
  const dismiss = async () => {
    playClickSound();
    if (current) {
      await supabase
        .from('badge_notifications')
        .update({ seen: true })
        .eq('id', current.id);
    }
    setCurrent(null);
  };

  // View all badges
  const viewBadges = async () => {
    playClickSound();
    if (current) {
      await supabase
        .from('badge_notifications')
        .update({ seen: true })
        .eq('id', current.id);
    }
    setCurrent(null);
    navigate('/collection');
  };

  // Trigger collector/organized check from outside
  // This is called by Collection.js after adding cards/binders
  useEffect(() => {
    const handler = (e) => {
      if (!activeProfile?.id) return;
      const { badgeType, count } = e.detail || {};
      if (!badgeType || !count) return;

      const config = BADGE_CONFIGS[badgeType];
      if (!config) return;

      if (config.oneOff) {
        if (count >= config.target) {
          // Check if we already notified
          supabase
            .from('badge_notifications')
            .select('id')
            .eq('user_id', activeProfile.id)
            .eq('badge_type', badgeType)
            .then(({ data }) => {
              if (!data || data.length === 0) {
                supabase
                  .from('badge_notifications')
                  .insert({ user_id: activeProfile.id, badge_type: badgeType, new_level: 1 })
                  .then(() => checkNotifications());
              }
            });
        }
      }
    };

    window.addEventListener('badge-check', handler);
    return () => window.removeEventListener('badge-check', handler);
  }, [activeProfile?.id, checkNotifications]);

  if (!current) return null;

  const badgeColor = LEVEL_COLORS[current.new_level] || LEVEL_COLORS[1];
  const msgFn = BADGE_MESSAGES[current.badge_type];
  const lines = msgFn ? msgFn(current.new_level) : [];

  // Build badge data for ring display
  const config = BADGE_CONFIGS[current.badge_type];
  const ringData = {};
  if (config) {
    if (config.oneOff) {
      const key = current.badge_type === 'collector' ? 'totalCards' : 'qualifyingBinders';
      ringData[key] = config.target; // Show as completed
    } else {
      const thresholds = config.thresholds || [5, 10, 25, 50, 100];
      const countKey = current.badge_type === 'trader' ? 'completedTrades'
        : current.badge_type === 'officer' ? 'successfulReports'
        : 'imageContributions';
      ringData[countKey] = thresholds[current.new_level - 1] || 0;
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 99999,
          padding: 20,
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 360,
          width: '100%',
        }}>
          {/* Chase character */}
          <motion.div
            style={{ marginBottom: 16 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: contentVisible ? 1 : 0, y: contentVisible ? 0 : 20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <img
              src={ChaseThumbsUp}
              alt="Chase"
              style={{ width: 120, height: 'auto', filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))' }}
            />
          </motion.div>

          {/* Badge ring - larger, centered above text box */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: contentVisible ? 1 : 0, scale: contentVisible ? 1 : 0.5 }}
            transition={{ duration: 0.5, delay: 0.2, type: 'spring', damping: 15 }}
            style={{ marginBottom: 16 }}
          >
            <BadgeProgressRing
              type={current.badge_type}
              data={ringData}
              size={80}
              showLabel={false}
              theme={theme}
            />
          </motion.div>

          {/* Text box - matching TutorialOverlay style */}
          <motion.div
            style={{
              background: 'white',
              border: '3px solid #374151',
              borderRadius: 8,
              padding: '14px 12px',
              width: '100%',
              minHeight: 130,
              position: 'relative',
              cursor: 'pointer',
            }}
            onClick={handleTextTap}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: contentVisible ? 1 : 0, y: contentVisible ? 0 : 15 }}
            transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
          >
            <div style={{
              fontFamily: "'PokeText', monospace",
              fontSize: 14,
              lineHeight: 1.5,
              color: '#1F2937',
              width: '100%',
            }}>
              {lines.map((line, idx) => {
                const displayLine = displayedText.split('\n')[idx] || '';
                // Check if this is the badge name line (all caps)
                const isBadgeLine = line === line.toUpperCase() && line.includes('LEVEL') || (line === 'COLLECTOR' || line === 'ORGANIZER');

                return (
                  <div
                    key={idx}
                    style={{
                      height: 21,
                      width: '100%',
                      whiteSpace: 'nowrap',
                      ...(isBadgeLine ? {
                        color: badgeColor,
                        fontWeight: 900,
                        fontSize: 16,
                        textAlign: 'center',
                        height: 24,
                      } : {}),
                    }}
                  >
                    {displayLine || '\u00A0'}
                  </div>
                );
              })}
            </div>
            {!isTypingComplete && (
              <span style={{
                position: 'absolute',
                bottom: 6,
                right: 10,
                fontSize: 9,
                color: '#9CA3AF',
                fontFamily: "'PokeText', monospace",
              }}>
                tap to skip
              </span>
            )}
          </motion.div>

          {/* Buttons */}
          <div style={{ width: '100%', minHeight: 116, marginTop: 16 }}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: buttonsVisible ? 1 : 0, y: buttonsVisible ? 0 : 10 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}
            >
              <button
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  backgroundColor: badgeColor,
                  border: 'none',
                  borderRadius: 10,
                  color: '#ffffff',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: "'PokeText', -apple-system, BlinkMacSystemFont, sans-serif",
                  pointerEvents: buttonsVisible ? 'auto' : 'none',
                }}
                onClick={dismiss}
              >
                Nice!
              </button>
              <button
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  backgroundColor: 'transparent',
                  border: '2px solid #ffffff',
                  borderRadius: 10,
                  color: '#ffffff',
                  fontSize: 16,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: "'PokeText', -apple-system, BlinkMacSystemFont, sans-serif",
                  pointerEvents: buttonsVisible ? 'auto' : 'none',
                }}
                onClick={viewBadges}
              >
                View All Badges
              </button>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
